import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box } from '@mui/material';
import { BrandContext } from '../../App';
import theme from '../../Theme';
import DefaultBadge from '../simple/DefaultBadge';
import PaginationControls from '../simple/PaginationControls';
import SimulationTile from '../simple/SimulationTile';

const ITEMS_PER_PAGE = 6;

const PastSimulationsCard = () => {
    const { brand, user, authToken, simulations } = useContext(BrandContext);
    const [currentPage, setCurrentPage] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = `Past Simulations - ${brand.name}`;
    }, [user, brand.name]);

    // use simulations from context
    const simulationsData = simulations?.all || [];
    const paginatedData = simulationsData.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    };

    const formatTime = (dateStr) => {
        const d = new Date(dateStr);
        return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const formatDuration = (startStr, endStr) => {
        const start = new Date(startStr);
        const end = new Date(endStr);
        const diffMs = end - start;

        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    };

    return (
        <Box sx={{ border: '2px solid', borderRadius: theme.shape.radius, borderColor: theme.palette.border.tertiary }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: theme.customSpacing.xl,
                p: theme.customSpacing['3xl'],
                pb: 0,
                alignItems: 'center',
            }}>
                <Typography variant="h6">Past Simulations</Typography>
                <DefaultBadge borderColor={theme.palette.border.brand} size='small' variant='pillTransparent'>{simulationsData.length} completed</DefaultBadge>
            </Box>

            <Box sx={{ pt: theme.customSpacing.xl }}>
                <Box sx={{
                    display: 'flex',
                    p: `${theme.customSpacing.xl} ${theme.customSpacing['3xl']}`,
                    borderTop: `1px solid ${theme.palette.border.tertiary}`,
                    borderBottom: `1px solid ${theme.palette.border.tertiary}`,
                    color: theme.palette.foreground.badge_icon,
                }}>
                    <Box sx={{
                        width: '50%',
                    }}>
                        <Typography sx={{
                            fontWeight: theme.typography.fontWeightMedium,
                            fontSize: theme.typography.textXS.fontSize,
                        }}>
                            Simulation Challenge
                        </Typography>
                    </Box>
                    <Box sx={{ width: '12.5%' }}>
                        <Typography sx={{
                            textAlign: 'center',
                            fontWeight: theme.typography.fontWeightMedium,
                            fontSize: theme.typography.textXS.fontSize,
                        }}>
                            Score
                        </Typography>
                    </Box>
                    <Box sx={{ width: '12.5%' }}>
                        <Typography sx={{
                            textAlign: 'center',
                            fontWeight: theme.typography.fontWeightMedium,
                            fontSize: theme.typography.textXS.fontSize,
                        }}>
                            Date completed
                        </Typography>
                    </Box>
                    <Box sx={{ width: '12.5%' }}>
                        <Typography sx={{
                            textAlign: 'center',
                            fontWeight: theme.typography.fontWeightMedium,
                            fontSize: theme.typography.textXS.fontSize,
                        }}>
                            Time completed
                        </Typography>
                    </Box>
                    <Box sx={{ width: '12.5%' }}>
                        <Typography sx={{
                            textAlign: 'center',
                            fontWeight: theme.typography.fontWeightMedium,
                            fontSize: theme.typography.textXS.fontSize,
                        }}>
                            Duration Simulation
                        </Typography>
                    </Box>
                </Box>

                {paginatedData.map((row) => (
                    <SimulationTile
                        key={row.id}
                        row={row}
                        theme={theme}
                        formatDate={formatDate}
                        formatTime={formatTime}
                        formatDuration={formatDuration}
                    />
                ))}
            </Box>

            <PaginationControls
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                simulations={simulationsData}
                ITEMS_PER_PAGE={ITEMS_PER_PAGE}
                theme={theme}
            />
        </Box>
    );
};

export default PastSimulationsCard;
